import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal } from '@mantine/core';
import { TbPlayerPlay } from 'react-icons/tb';

import { useWorkflowChrome } from '../WorkflowChromeContext';
import { usePeriodById } from '../../../services/period.service';
import { useGetWorkflowById } from '../../../services/workflow.services';
import { useUserGroupsCurrentUserAdminState } from '../../../services/user.service';
import { permissionPolicies } from '../../../permissions/policies';
import { useIsV2Surface } from '../../../utils/design';
import { formatPeriodLabel } from '../../../utils/periodLabel';
import { buildStagePath } from '../../../utils/workflowNavigation';
import { canRunPeriod, placeFor, samePlace, STAGE_FOR_STEP, stepLabels, stepsAhead } from './steps';
import { usePeriodRun } from './PeriodRunContext';
import { useReportHealth } from './reportHealth';
import { Ask, Band, Fill } from './style';

const BAND_HEIGHT = '36px';

/* The run, said in one line, above everything else on the page. */
/* How long the band waits for the report's own page to count itself before saying the run is over. */
const REPORT_DRAW_MS = 12_000;

const RunBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chrome = useWorkflowChrome();
  const isV2 = useIsV2Surface(location.pathname, location.search);
  const {
    run,
    period: runPeriod,
    state,
    narration,
    question,
    outcome,
    isStalled,
    isFollowing,
    start,
    stop,
    confirm,
    retry,
    dismiss,
    resumeFollowing,
    go,
    moveOn,
    isDriver,
    refire,
    waitingNote,
    stuckMinutes,
    reExtract,
    retrySheets,
    troubled,
    canRetrySheets,
    isRetrying
  } = usePeriodRun();

  const { data: chromePeriod } = usePeriodById({
    periodId: chrome.periodId,
    queryParams: { enabled: !!chrome.periodId }
  });
  const { data: workflow } = useGetWorkflowById({
    id: chrome.workflowId,
    queryParams: { enabled: !!chrome.workflowId }
  });
  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  /* The step names the stepper under this band uses — they depend on the report type. */
  const labels = stepLabels(workflow?.report_type ?? run?.reportType);

  const canManage = permissionPolicies.checkAccess({
    currentUserRole: currentUser?.role,
    currentUserId: currentUser?.azure_object_id,
    assignedTo: workflow?.assigned_to
  });

  const outcomeHere = !!outcome?.workflowId && location.pathname.includes(outcome.workflowId);

  /* The server says the report exists a while before the page draws it, and "everything has run"
     over the placeholders is the run talking over its own result. */
  const health = useReportHealth();
  const onReportPage = /\/report/.test(location.pathname);
  const [waitedForReport, setWaitedForReport] = useState(false);
  useEffect(() => {
    if (!outcome || outcome.kind === 'error' || !onReportPage || health) return undefined;
    setWaitedForReport(false);
    const timer = setTimeout(() => setWaitedForReport(true), REPORT_DRAW_MS);
    return () => clearTimeout(timer);
  }, [outcome, onReportPage, health]);
  const reportStillDrawing =
    !!outcome && outcome.kind !== 'error' && onReportPage && !health && !waitedForReport;
  /* Outside its own project the band named a period nobody there was looking at. The question
     below still finds the reader anywhere. */
  const runHere = !!run?.workflowId && location.pathname.includes(run.workflowId);

  /* Nothing to offer on a filed period, and nothing to offer in the classic design either. */
  const offer =
    !run &&
    // ...and not silenced by an outcome from somewhere else: that period is still worth running.
    !outcomeHere &&
    isV2 &&
    canManage &&
    !!chrome.periodId &&
    !!chromePeriod &&
    /* And not before the report type is known: the chain a run takes depends on it. */
    !!workflow?.report_type &&
    chromePeriod.submit_progress_status !== 'Completed' &&
    canRunPeriod(chromePeriod, workflow.report_type);

  const shown = runHere || outcomeHere || offer;

  useEffect(() => {
    const root = document.documentElement;
    if (shown) root.style.setProperty('--dx-run-band', BAND_HEIGHT);
    else root.style.removeProperty('--dx-run-band');
    return () => root.style.removeProperty('--dx-run-band');
  }, [shown]);

  /* The stage before the one being asked about is the one holding something unread. */
  const reviewPlace = () => {
    if (!question || !run) return null;
    const step = question.step === 'report' ? 'transformation' : 'extraction';
    /* The stage the question is about — for a conversion the workbook, not the step before it. */
    const stage = question.reviewStage || STAGE_FOR_STEP[step];
    return buildStagePath(stage, run.workflowId, run.periodId, runPeriod, workflow);
  };

  const ask = question && (
    <Modal
      opened
      onClose={() => {}}
      closeOnEscape={false}
      closeOnClickOutside={false}
      withCloseButton={false}
      centered
      title="Before it goes on"
      size={560}
      zIndex={1100}
    >
      <Ask>
        <b>{question.title}.</b>
        {/* Which ones. The count alone sent the reader out of the run and into the issues drawer to
            find out what they were being asked about — to answer a question that was holding the
            run up. The records the tiles are drawn from carry the sheet, its file and the server's
            own message, and they were already in hand. */}
        {troubled.length > 0 && (
          <ul className="sheets">
            {troubled.slice(0, 6).map(item => (
              <li key={item.key}>
                <b>{item.sheet}</b>
                {item.file ? <em> · {item.file}</em> : null}
                {item.why || item.state ? (
                  <span> — {item.why || item.state.toLowerCase()}</span>
                ) : null}
              </li>
            ))}
            {troubled.length > 6 && <li className="more">and {troubled.length - 6} more</li>}
          </ul>
        )}
        <p>{question.body}</p>
        <div className="buttons">
          <Button
            variant="default"
            onClick={() => {
              const to = reviewPlace();
              stop();
              if (to) navigate(to);
            }}
          >
            Stop and review
          </Button>
          <button type="button" className="plain spacer" onClick={stop}>
            Stop here
          </button>
          {/* Doing the short sheets again is the better answer where it is available, so it is the
              one the button reads. Building the report on the half that worked stays on offer next
              to it — sometimes the sheets that failed are ones nobody is going to fix today. */}
          {question.retry ? (
            <>
              <Button variant="default" onClick={confirm}>
                Continue anyway
              </Button>
              <Button onClick={reExtract} loading={isRetrying} disabled={!canRetrySheets}>
                {retrySheets.length ? `Extract ${retrySheets.length} again` : 'Extract them again'}
              </Button>
            </>
          ) : (
            <Button onClick={confirm}>Continue</Button>
          )}
        </div>
      </Ask>
    </Modal>
  );

  if (!shown) return ask;

  if (outcomeHere) {
    const isError = outcome.kind === 'error';
    const reportPlace = placeFor({ status: 'step', step: 'report' }, outcome);
    return (
      <>
        <Band data-tone={isError ? 'error' : 'done'}>
          <i className="dot" aria-hidden />
          <span className="what">
            {/* The outcome says what happened to it; only the plain "it would not start" case has
                no words of its own. A step that ran twice and produced nothing did start, and the
                headline used to say otherwise directly above a body that said so. */}
            {isError
              ? outcome.headline || `${labels[outcome.step] || 'The run'} could not start`
              : reportStillDrawing
                ? 'The report is built — opening it'
                : 'Everything that could run has run'}
          </span>
          <em>
            {isError
              ? outcome.text
              : reportStillDrawing
                ? 'a moment while it draws'
                : 'What is left — approval, submission — is a decision.'}
          </em>
          <span className="right">
            {isError ? (
              <button type="button" className="plain" onClick={retry}>
                Try again
              </button>
            ) : (
              /* Not when they are already looking at it. */
              reportPlace &&
              !samePlace(`${location.pathname}${location.search}`, reportPlace) && (
                <button
                  type="button"
                  className="plain"
                  onClick={() => {
                    dismiss();
                    navigate(reportPlace);
                  }}
                >
                  Open it
                </button>
              )
            )}
            <button type="button" className="plain" onClick={dismiss}>
              Close
            </button>
          </span>
        </Band>
        {ask}
      </>
    );
  }

  if (!run) {
    return (
      <>
        <Band data-tone="offer">
          <i className="dot" aria-hidden />
          {/* "The rest" is right on a period part-way through and wrong on a new one, where there
              is no rest — the offer is made there too, deliberately: what it is waiting for is the
              files, and it will wait on the page where they go. */}
          <span className="what">
            {chromePeriod?.file_count
              ? 'The rest of this period can run by itself'
              : 'This period can run by itself'}
          </span>
          <em>every step the machine can take, one after another, up to the report</em>
          <span className="right">
            <Button
              size="compact-xs"
              variant="light"
              leftSection={<TbPlayerPlay size={12} />}
              onClick={() =>
                start({
                  workflowId: chrome.workflowId,
                  periodId: chrome.periodId,
                  reportType: workflow?.report_type,
                  period: chromePeriod
                })
              }
            >
              Run everything
            </Button>
          </span>
        </Band>
        {ask}
      </>
    );
  }

  const waiting = state?.status === 'blocked' || isStalled || !!stuckMinutes;
  const finished = state?.status === 'settled';
  const progress = narration?.progress;

  /* "then the rest runs by itself" belongs only to a step that has a rest after it. */
  const running = state?.status === 'busy' || state?.status === 'step' || state?.status === 'asked';

  /* The run is on one stage and the reader is standing on another. */
  const elsewhere =
    !isFollowing &&
    !!state?.step &&
    !!chrome.activeStageKey &&
    chrome.activeStageKey !== STAGE_FOR_STEP[state.step];
  const tail = running && stepsAhead(runPeriod, run.reportType).length > 0;
  /* Whatever the server said comes first, waiting or stalled. */
  const detail = isStalled
    ? waitingNote || 'the server has not picked it up'
    : [
        stuckMinutes ? `nothing has moved for ${stuckMinutes} minutes` : null,
        waitingNote || narration?.detail,
        !stuckMinutes && tail && 'then the rest runs by itself'
      ]
        .filter(Boolean)
        .join(' · ');

  return (
    <>
      <Band data-tone={finished ? 'done' : waiting ? 'waiting' : 'running'}>
        <i className="dot" aria-hidden />
        {runPeriod && <span className="period">{formatPeriodLabel(runPeriod)}</span>}
        <span className="what" role="status" aria-atomic="true">
          {isStalled && state?.status === 'step'
            ? `${labels[state.step] || 'That step'} has not started yet`
            : narration?.text || 'Starting'}
        </span>
        <em>{[detail, elsewhere && 'you are on another step'].filter(Boolean).join(' · ')}</em>
        <span className="right">
          {/* Said, not hidden: this tab shows the run and follows it, but another tab is the one
              asking for the steps. Without the line, a duplicated tab looks like a run that has
              stopped responding to anything on it. */}
          {!isDriver && <span className="period">another tab is driving this</span>}
          {/* The press lives here rather than in a card down the page: a card has to guess when the
              files are finished, and it guesses by appearing under them. The strip is already saying
              the run is holding, so the way on belongs in the same line. */}
          {state?.status === 'ready' && isDriver && (
            <button type="button" className="go" onClick={go}>
              Start
            </button>
          )}
          {!isFollowing && (
            <button type="button" className="plain" onClick={resumeFollowing}>
              Follow it
            </button>
          )}
          {isStalled && (
            <button type="button" className="plain" onClick={refire}>
              Ask again
            </button>
          )}
          {/* The way out of a stage the server has abandoned. Offered only once the run has been
              standing still long enough to say so, and never taken by the run itself: from here a
              stopped stage and a large sheet taking its time look the same. */}
          {(isStalled || !!stuckMinutes) && (
            <button type="button" className="plain" onClick={moveOn}>
              Stop waiting
            </button>
          )}
          {run.stopAfter ? (
            <span className="period">stopping after this step</span>
          ) : (
            <button type="button" className="plain" onClick={stop}>
              Stop after this step
            </button>
          )}
        </span>
        {progress && (
          <Fill style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
        )}
      </Band>
      {ask}
    </>
  );
};

export default RunBanner;
